import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { fmtDateTime, inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Inventory() {
  const [rows, setRows] = useState([]);
  const [logs, setLogs] = useState([]);
  const [adjust, setAdjust] = useState(null); // row
  const [delta, setDelta] = useState(0);
  const [reason, setReason] = useState("");

  const load = () => {
    api.get("/admin/inventory").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/inventory-logs").then((r) => setLogs(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const save = async () => {
    if (!delta || !reason.trim()) { toast.error("Enter a quantity change and reason"); return; }
    try {
      await api.post("/admin/inventory/adjust", {
        product_id: adjust.product_id, variant_id: adjust.variant_id,
        delta: Number(delta), reason,
      });
      toast.success("Stock adjusted");
      setAdjust(null); setDelta(0); setReason("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-inventory-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Inventory</h1>

      <div className="bg-white border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-[11px] font-mono uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
              <th className="p-3">Product</th><th className="p-3">Variant</th><th className="p-3">SKU</th>
              <th className="p-3">Price</th><th className="p-3">Stock</th><th className="p-3 text-right">Adjust</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.variant_id} className="border-b border-neutral-100" data-testid={`inventory-row-${r.sku}`}>
                <td className="p-3 font-medium">{r.title}</td>
                <td className="p-3 font-mono text-xs">{Object.entries(r.options || {}).map(([k, v]) => `${k}: ${v}`).join(" / ") || "Default"}</td>
                <td className="p-3 font-mono text-xs">{r.sku}</td>
                <td className="p-3">{inr(r.price)}</td>
                <td className={`p-3 font-mono font-semibold ${r.stock === 0 ? "text-red-600" : r.stock <= 5 ? "text-amber-600" : ""}`} data-testid={`stock-${r.sku}`}>{r.stock}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => { setAdjust(r); setDelta(0); setReason(""); }} data-testid={`adjust-${r.sku}`}>Adjust</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <section className="bg-white border border-neutral-200 p-5">
        <h2 className="font-heading font-semibold mb-3">Recent Adjustments</h2>
        {logs.length === 0 ? (
          <p className="text-sm text-neutral-500">No adjustments logged yet.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {logs.map((l) => (
              <li key={l.id} className="flex flex-wrap gap-x-4 border-b border-neutral-100 pb-2" data-testid={`log-${l.id}`}>
                <span className={`font-mono font-semibold ${l.delta > 0 ? "text-emerald-700" : "text-red-600"}`}>{l.delta > 0 ? "+" : ""}{l.delta}</span>
                <span className="text-neutral-700">{l.reason}</span>
                <span className="text-xs text-neutral-400 font-mono">{l.by} · {fmtDateTime(l.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Dialog open={!!adjust} onOpenChange={() => setAdjust(null)}>
        <DialogContent className="sm:max-w-sm" aria-describedby={undefined} data-testid="adjust-dialog">
          <DialogHeader><DialogTitle className="font-heading">Adjust Stock — {adjust?.sku}</DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-500">Current stock: <span className="font-mono font-semibold">{adjust?.stock}</span></p>
          <div>
            <Label>Change (+/-)</Label>
            <Input type="number" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="e.g. 10 or -3" data-testid="adjust-delta-input" />
          </div>
          <div>
            <Label>Reason</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. New stock arrived / Damaged" data-testid="adjust-reason-input" />
          </div>
          <Button onClick={save} className="w-full bg-neutral-900 hover:bg-brand text-white btn-press" data-testid="adjust-save-btn">
            Save Adjustment
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
